import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useGetClientsQuery } from '../../../../../redux/services/clients/clientsApi';

import { Loader } from '../../../../ui/Loader';
import { SearchItemClients } from '../SearchItemClients';
import { extractPageFromUrl } from '../../../../../utils/utils';
import { toast } from 'sonner';
import { errorMessages } from '../../../../../data/constants';

export const SearchListClients = ({
  setIsOpen,
  setClosedManuallySearch = () => {},
  formProps = {},
  inputName = '',
  onClientSelect = null,
}) => {
  const [page, setPage] = useState(1);
  const [accumulatedClients, setAccumulatedClients] = useState([]);
  const listRef = useRef(null);

  const userToken = useSelector(selectUserToken);
  const {
    data: clients = {},
    isLoading,
    isFetching,
    error: clientsError,
  } = useGetClientsQuery(
    {
      token: userToken,
      activeSeachType: inputName === 'find_deal' ? 2 : '',
      page,
      isPagination: true,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (clients?.data && Array.isArray(clients.data)) {
      if (page === 1) {
        setAccumulatedClients(clients.data);
      } else {
        setAccumulatedClients(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newClients = clients.data.filter(c => !existingIds.has(c.id));
          return [...prev, ...newClients];
        });
      }
    }
  }, [clients?.data, page]);

  const onScroll = useCallback(() => {
    if (!listRef.current || isFetching) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const scrolledToEnd = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToEnd && clients?.links?.next) {
      const nextPage = extractPageFromUrl(clients.links.next);
      if (nextPage && nextPage !== page) {
        setPage(nextPage);
      }
    }
  }, [isFetching, clients?.links?.next, page]);

  useEffect(() => {
    function handleClickOutside(event) {
      const openDialogs = document.querySelectorAll('.dialog');
      const topDialog = openDialogs[openDialogs.length - 1];
      if (topDialog && !topDialog.querySelector('.dialog-with-click')) {
        return;
      }

      if (
        listRef.current &&
        !listRef.current.contains(event.target) &&
        !event.target.classList.contains('exclude-click')
      ) {
        setIsOpen(false);
        setClosedManuallySearch(true);

        setTimeout(() => {
          setClosedManuallySearch(false);
        }, 100);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [listRef, setIsOpen, setClosedManuallySearch]);

  const filteredClients = useMemo(() => {
    const searchValue = formProps?.values?.[inputName] || '';

    if (!searchValue || typeof searchValue === 'number') {
      return accumulatedClients;
    }

    return accumulatedClients.filter(client => {
      const clientRequestRealtyIds = client.requests?.map(r => r.id) || [];
      const searchableFields = [
        client.first_name,
        client.last_name,
        `${client.first_name} ${client.last_name}`,
        client.email,
        client.email2,
        client.phone,
        client.phone2,
        client.phone3,
        client.office_phone,
        client.office_phone2,
        client.address,
        client.city_id?.name,
        ...clientRequestRealtyIds,
      ]
        .filter(Boolean)
        .map(field => String(field).toLowerCase())
        .join(' ');

      return searchableFields.includes(searchValue?.toLowerCase());
    });
  }, [accumulatedClients, formProps?.values, inputName]);

  const hasClients = filteredClients.length > 0;
  const showEmpty = !isLoading && !hasClients && !clientsError;

  useEffect(() => {
    if (clientsError) {
      toast.error(errorMessages.wentWrong, {
        error: true,
        action: {},
      });
    }
  }, [clientsError]);
  return (
    <div
      ref={listRef}
      onScroll={onScroll}
      className={`absolute top-[42px] left-0 pb-2 max-h-[500px] border-[1px] b-gray-200 bg-white shadow-md z-[4] overflow-y-auto hide-scroll ${inputName === 'find_deal' ? 'max-md:left-[-2rem]' : ''}`}
    >
      {hasClients && !isLoading && (
        <>
          {filteredClients.map(item => (
            <SearchItemClients
              key={item.id}
              item={item}
              formProps={formProps}
              inputName={inputName}
              setIsOpen={setIsOpen}
              onClientSelect={onClientSelect}
            />
          ))}
        </>
      )}
      {isLoading && (
        <div className="flex justify-center pt-3 pb-1.5 px-1.5 w-full">
          <Loader
            width={26}
            height={26}
          />
        </div>
      )}

      {showEmpty && (
        <div className="flex justify-center pt-3 pb-1.5 px-1.5 w-full">
          <span className="text-sm font-medium text-blackColor">
            Nothing found
          </span>
        </div>
      )}
    </div>
  );
};
